/**
 * Normalizes a CredentialMaster API item to a consistent camelCase shape.
 * @param {object} item
 * @returns {{ id: number|string, credentialName: string, type: number, isDeletable: boolean }|null}
 */
export function normalizeCredentialMaster(item) {
  if (!item || typeof item !== "object") {
    return null;
  }

  const id = item.id ?? item.Id;
  if (id == null || id === "") {
    return null;
  }

  return {
    id,
    credentialName:
      item.credentialName ?? item.CredentialName ?? item.name ?? "",
    type: Number(
      item.type ?? item.Type ?? item.credentialType ?? item.CredentialType,
    ),
    isDeletable: item.isDeletable ?? item.IsDeletable ?? true,
  };
}

/**
 * @param {unknown} data
 * @returns {{ id: number|string, credentialName: string, type: number, isDeletable: boolean }[]}
 */
export function normalizeCredentialMasterList(data) {
  const list = Array.isArray(data)
    ? data
    : Array.isArray(data?.data)
      ? data.data
      : [];
  return list.map(normalizeCredentialMaster).filter(Boolean);
}

/**
 * Extracts a created/updated CredentialMaster from a mutation response.
 * @param {object} response
 * @returns {{ id: number|string, credentialName: string, type: number, isDeletable: boolean }|null}
 */
export function extractCredentialMasterFromResponse(response) {
  return (
    normalizeCredentialMaster(response?.data) ||
    normalizeCredentialMaster(response) ||
    null
  );
}

/**
 * Nested CredentialMaster from a license/certificate listing or detail record.
 * @param {object} [record]
 * @returns {object|null}
 */
export function getNestedCredentialMaster(record) {
  if (!record || typeof record !== "object") {
    return null;
  }
  return record.credentialMaster ?? record.CredentialMaster ?? null;
}

/**
 * Display name for a credential listing/detail row.
 * Prefer `credentialMaster.credentialName` — some APIs leave the top-level
 * `credentialName` empty even when the nested master has a name.
 * @param {object} [record]
 * @returns {string}
 */
export function getCredentialDisplayName(record) {
  const master = getNestedCredentialMaster(record);
  const fromMaster =
    master?.credentialName ?? master?.CredentialName ?? master?.name ?? "";
  const fromRecord = record?.credentialName ?? record?.CredentialName ?? "";
  return String(fromMaster || fromRecord).trim();
}

/**
 * CredentialMaster id for a listing/detail row (edit form pre-select).
 * @param {object} [record]
 * @returns {number|string}
 */
export function getCredentialMasterIdFromRecord(record) {
  const master = getNestedCredentialMaster(record);
  return (
    record?.credentialMasterId ??
    record?.CredentialMasterId ??
    master?.id ??
    master?.Id ??
    ""
  );
}

/**
 * Whether a credential license was added by another Account Owner.
 * Reads `isOtherOwner` from the CredentialLicense API (camelCase or PascalCase).
 * @param {{ isOtherOwner?: boolean, IsOtherOwner?: boolean }|null|undefined} credential
 * @returns {boolean}
 */
export function isOtherOwnerCredential(credential) {
  if (!credential || typeof credential !== "object") {
    return false;
  }
  return Boolean(credential.isOtherOwner ?? credential.IsOtherOwner);
}
